import { useEffect, useState } from "react";
import { ShoppingCart, Banknote } from "lucide-react";
import { TxnBadge } from "@/components/common";
import { isoToDmy } from "@/components/DatePicker";
import { SlipButton } from "@/components/advance/SlipActions";
import { api } from "@/lib/api";
import { inr, fmtDateTime, ADVANCE_ONLY } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function RecentAdvances({ refreshKey }) {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const ownOnly = ADVANCE_ONLY.includes(user?.role);
  useEffect(() => {
    api.get("/advances", { params: { limit: 30 } }).then((r) => setData(r.data)).catch(() => setData({ items: [], totals: null, own_only: ownOnly }));
  }, [refreshKey, ownOnly]);
  if (!data) return null;
  const tot = (p) => data.totals?.[p] || { total: 0, count: 0 };
  return (
    <div className="space-y-4">
      {data.totals && !ownOnly && (
      <div className="grid sm:grid-cols-2 gap-3">
        {[["Purchase", ShoppingCart], ["Cash Advance", Banknote]].map(([p, Icon]) => (
          <div key={p} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between" data-testid={`advance-total-${p.replace(" ", "-").toLowerCase()}`}>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{p === "Purchase" ? "Purchase advances" : "Cash advances"} given</div>
              <div className="money text-xl font-bold text-slate-900 mt-1">{inr(tot(p).total)}</div>
              <div className="text-xs text-slate-500">{tot(p).count} {tot(p).count === 1 ? "entry" : "entries"}</div>
            </div>
            <Icon className="h-6 w-6 text-blue-800" />
          </div>
        ))}
      </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200" data-testid="recent-advances">
        <div className="px-5 py-3 border-b border-slate-100 font-heading font-semibold text-slate-800" data-testid="recent-advances-title">{data.own_only ? "My recent advances" : "Recent advances"}</div>
        {data.items.length === 0 ? <div className="px-5 py-8 text-sm text-slate-500 text-center" data-testid="recent-advances-empty">{data.own_only ? "You haven't added any advances yet." : "No advances added yet."}</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>{["Date", "Employee", "Purpose", "Amount", "Added", ""].map((h, i) => <th key={i} className={`px-4 py-2.5 ${i === 3 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100" data-testid={`recent-advance-${t.id}`}>
                    <td className="px-4 py-2.5 money">{isoToDmy(t.date)}</td>
                    <td className="px-4 py-2.5"><span className="money text-blue-800 font-semibold">{t.employee_code}</span> {t.employee_name}</td>
                    <td className="px-4 py-2.5">{t.purpose}{t.reversed && <span className="ml-2"><TxnBadge type="reversal" reversed /></span>}</td>
                    <td className="px-4 py-2.5 text-right money font-semibold">{inr(t.debit)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{fmtDateTime(t.created_at)} · {t.created_by}</td>
                    <td className="px-4 py-2.5 text-right"><SlipButton ids={[t.id]} label="Reprint slip" testid={`recent-advance-print-${t.id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
